import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { MediaPlayer, MediaProvider, PlayButton, Track, useMediaState } from "@vidstack/react";
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from "@vidstack/react/player/layouts/default";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/video.css";
import VideoProgressManager from "./videoProgress.js";
import playIconUrl from "../../../images/youtube-play.svg";
import pauseIconUrl from "../../../images/youtube-pause.svg";

const videoProgressManager = new VideoProgressManager();

// Center play/pause flash — shows icon briefly on state change, then fades out
const CenterPlayPauseFlash = () => {
  const paused = useMediaState("paused");
  const started = useMediaState("started");
  const [flash, setFlash] = useState(null); // 'play' | 'pause' | null
  const prevPausedRef = useRef(paused);

  useEffect(() => {
    // Skip the initial mount / before playback starts
    if (!started) {
      prevPausedRef.current = paused;
      return;
    }
    if (paused === prevPausedRef.current) return;
    prevPausedRef.current = paused;

    // Show the icon for the action that just happened
    setFlash(paused ? "pause" : "play");
    const id = setTimeout(() => setFlash(null), 1200);
    return () => clearTimeout(id);
  }, [paused, started]);

  if (!flash) return null;

  return (
    <div className="vds-center-flash" key={flash}>
      <img
        src={flash === "play" ? playIconUrl : pauseIconUrl}
        alt={flash === "play" ? "Play" : "Pause"}
        draggable={false}
      />
    </div>
  );
};

// Sanitize YouTube ID - remove query parameters, fragments, and whitespace
const sanitizeYouTubeId = (id) => {
  if (!id) return null;
  const cleanId = id.split("?")[0].split("#")[0].trim();
  const match = cleanId.match(/^[a-zA-Z0-9_-]{6,20}$/);
  return match ? match[0] : cleanId;
};

// Detect iOS (including iPad on iOS 13+)
const isIOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// iPhone/iPod — no Fullscreen API, Vidstack UI conflicts with YouTube iframe.
// Fall back to native YouTube iframe with its own controls + native fullscreen.
const isIPhone = /iPhone|iPod/.test(navigator.userAgent);

// Temporary feature flag — set to false to re-enable Vidstack on non-iPhone devices
const USE_NATIVE_YOUTUBE_ALL = true;

// ---------------------------------------------------------------------------
// iPhone fallback: plain YouTube iframe with native controls
// ---------------------------------------------------------------------------
const IPhoneYouTubePlayer = forwardRef(
  (
    {
      videoId,
      onLoadingChange,
      onTimeUpdate,
      onEnded,
      onPlaying,
      onFullscreenChange,
      onFullscreenError,
      skipResume,
    },
    ref,
  ) => {
    const containerRef = useRef(null);
    const ytPlayerRef = useRef(null);
    const hasResumedRef = useRef(false);
    const timeIntervalRef = useRef(null);
    const cleanVideoId = sanitizeYouTubeId(videoId);

    // Load YouTube IFrame API
    useEffect(() => {
      if (!window.YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        tag.async = true;
        document.head.appendChild(tag);
      }
    }, []);

    // Create player
    useEffect(() => {
      if (!cleanVideoId) return;

      const createPlayer = () => {
        if (!containerRef.current) return;

        const div = document.createElement("div");
        containerRef.current.innerHTML = "";
        containerRef.current.appendChild(div);

        ytPlayerRef.current = new window.YT.Player(div, {
          videoId: cleanVideoId,
          playerVars: {
            autoplay: 0,
            playsinline: 0, // native fullscreen on iPhone
            rel: 0,
            modestbranding: 1,
            fs: 1,
            controls: 1,
          },
          events: {
            onReady: (e) => {
              onLoadingChange?.(false);
              // Resume from progress
              if (!hasResumedRef.current && !skipResume) {
                hasResumedRef.current = true;
                const resumeTime = videoProgressManager.getResumeTime(cleanVideoId);
                if (resumeTime > 0) {
                  setTimeout(() => {
                    try { e.target.seekTo(resumeTime, true); } catch (_) {}
                  }, 500);
                }
              }
            },
            onStateChange: (e) => {
              if (e.data === 0) onEnded?.();        // ENDED
              if (e.data === 1) onPlaying?.();       // PLAYING
            },
          },
        });

        // Time tracking
        timeIntervalRef.current = setInterval(() => {
          try {
            const player = ytPlayerRef.current;
            if (!player || typeof player.getCurrentTime !== "function") return;
            const ct = player.getCurrentTime();
            const dur = player.getDuration();
            if (ct > 0 && dur > 0) {
              videoProgressManager.updateProgress(cleanVideoId, ct, dur);
            }
            onTimeUpdate?.(ct);
          } catch (_) {}
        }, 1000);
      };

      if (window.YT && window.YT.Player) {
        createPlayer();
      } else {
        window.onYouTubeIframeAPIReady = createPlayer;
      }

      return () => {
        clearInterval(timeIntervalRef.current);
        try { ytPlayerRef.current?.destroy(); } catch (_) {}
        ytPlayerRef.current = null;
      };
    }, [cleanVideoId]);

    // Expose ref API matching the VideoModal contract
    useImperativeHandle(ref, () => ({
      get currentTime() {
        try { return ytPlayerRef.current?.getCurrentTime() || 0; } catch (_) { return 0; }
      },
      set currentTime(t) {
        try { ytPlayerRef.current?.seekTo(t, true); } catch (_) {}
      },
      seek(t) {
        try { ytPlayerRef.current?.seekTo(t, true); } catch (_) {}
      },
      get ended() {
        try { return ytPlayerRef.current?.getPlayerState() === 0; } catch (_) { return false; }
      },
      get duration() {
        try { return ytPlayerRef.current?.getDuration() || 0; } catch (_) { return 0; }
      },
      play() { try { ytPlayerRef.current?.playVideo(); } catch (_) {} },
      pause() { try { ytPlayerRef.current?.pauseVideo(); } catch (_) {} },
      toggleFullscreen() { /* handled natively by YouTube on iPhone */ },
      unmute() { try { ytPlayerRef.current?.unMute(); } catch (_) {} },
    }), []);

    return <div ref={containerRef} className="youtube-native-player" />;
  },
);
IPhoneYouTubePlayer.displayName = "IPhoneYouTubePlayer";

// ---------------------------------------------------------------------------
// Main Vidstack player (iPad + desktop)
// ---------------------------------------------------------------------------
const VidstackPlayer = forwardRef(
  (
    {
      videoId,
      onLoadingChange,
      onTimeUpdate,
      onFullscreenChange,
      onFullscreenError,
      onEnded,
      onPlaying,
      isMobile,
      autoplay = false,
      skipResume = false,
      subtitleUrl,
      title,
      source = 'youtube',
      fileUrl = '',
      featuredImageUrl = '',
    },
    ref,
  ) => {
    const playerRef = useRef(null);
    const hasResumedRef = useRef(false);
    const cleanVideoId = sanitizeYouTubeId(videoId);
    const isFileSource = source === 'file';

    // Progress-resume key: YT ID for youtube source, file URL for file source
    const progressKey = isFileSource ? fileUrl : cleanVideoId;

    // Poster: YouTube thumbnail for yt source, featured image for file source
    const [posterUrl, setPosterUrl] = useState(
      isFileSource
        ? featuredImageUrl
        : `https://img.youtube.com/vi/${cleanVideoId}/hqdefault.jpg`
    );
    useEffect(() => {
      if (isFileSource) {
        setPosterUrl(featuredImageUrl || '');
        return;
      }
      if (!cleanVideoId) return;
      const maxres = `https://img.youtube.com/vi/${cleanVideoId}/maxresdefault.jpg`;
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth > 120) {
          setPosterUrl(maxres);
        }
      };
      img.src = maxres;
    }, [cleanVideoId, isFileSource, featuredImageUrl]);

    // iPhone (or native-iframe flag) → render native YouTube iframe,
    // but ONLY for the YouTube source. File source always uses Vidstack
    // (MP4 plays natively on iOS inside Vidstack).
    if (!isFileSource && (isIPhone || USE_NATIVE_YOUTUBE_ALL)) {
      return (
        <IPhoneYouTubePlayer
          ref={ref}
          videoId={videoId}
          onLoadingChange={onLoadingChange}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          onPlaying={onPlaying}
          onFullscreenChange={onFullscreenChange}
          onFullscreenError={onFullscreenError}
          skipResume={skipResume}
        />
      );
    }

    // Reset hasResumed when source identity changes
    useEffect(() => {
      hasResumedRef.current = false;
    }, [progressKey]);

    // Expose ref methods matching the contract VideoModal.jsx expects
    useImperativeHandle(
      ref,
      () => ({
        get currentTime() {
          try {
            return playerRef.current?.state.currentTime ?? 0;
          } catch (error) {
            console.warn("Error getting currentTime:", error);
            return 0;
          }
        },
        set currentTime(time) {
          try {
            if (playerRef.current) {
              playerRef.current.currentTime = time;
            }
          } catch (error) {
            console.warn("Error setting currentTime:", error);
          }
        },
        seek(time) {
          try {
            if (playerRef.current) {
              playerRef.current.currentTime = time;
            }
          } catch (error) {
            console.warn("Error seeking:", error);
          }
        },
        get ended() {
          try {
            return playerRef.current?.state.ended ?? false;
          } catch (error) {
            console.warn("Error checking ended state:", error);
            return false;
          }
        },
        get duration() {
          try {
            return playerRef.current?.state.duration ?? 0;
          } catch (error) {
            console.warn("Error getting duration:", error);
            return 0;
          }
        },
        play() {
          try {
            playerRef.current?.play();
          } catch (error) {
            console.warn("Error playing video:", error);
          }
        },
        pause() {
          try {
            playerRef.current?.pause();
          } catch (error) {
            console.warn("Error pausing video:", error);
          }
        },
        toggleFullscreen() {
          try {
            if (playerRef.current?.state.fullscreen) {
              playerRef.current.exitFullscreen();
            } else {
              playerRef.current?.enterFullscreen();
            }
          } catch (error) {
            console.warn("Error toggling fullscreen:", error);
            onFullscreenError?.(error);
          }
        },
        unmute() {
          try {
            if (playerRef.current) {
              playerRef.current.muted = false;
            }
          } catch (error) {
            console.warn("Error unmuting video:", error);
          }
        },
      }),
      [],
    );

    // Resume from saved progress when player is ready
    const handleCanPlay = useCallback(() => {
      onLoadingChange?.(false);

      if (!hasResumedRef.current && !skipResume && progressKey) {
        hasResumedRef.current = true;
        const resumeTime = videoProgressManager.getResumeTime(progressKey);
        if (resumeTime > 0 && playerRef.current) {
          setTimeout(() => {
            try {
              playerRef.current.currentTime = resumeTime;
            } catch (e) {
              console.warn("Error seeking to resume time:", e);
            }
          }, 500);
        }
      }
    }, [progressKey, onLoadingChange, skipResume]);

    // Track progress and forward time updates
    const handleTimeUpdate = useCallback(
      (event) => {
        const detail = event?.detail;
        const currentTime = detail?.currentTime ?? playerRef.current?.state.currentTime ?? 0;
        const duration = playerRef.current?.state.duration ?? 0;

        if (progressKey && currentTime > 0 && duration > 0) {
          videoProgressManager.updateProgress(progressKey, currentTime, duration);
        }

        onTimeUpdate?.(currentTime);
      },
      [progressKey, onTimeUpdate],
    );

    const handleFullscreenChange = useCallback(
      (event) => {
        const isFullscreen = event?.detail ?? false;
        onFullscreenChange?.(isFullscreen);
      },
      [onFullscreenChange],
    );

    const handleFullscreenError = useCallback(
      (event) => {
        const error = event?.detail ?? new Error("Fullscreen error");
        onFullscreenError?.(error);
      },
      [onFullscreenError],
    );

    const handleEnded = useCallback(() => {
      onEnded?.();
    }, [onEnded]);

    const handlePlaying = useCallback(() => {
      onPlaying?.();
    }, [onPlaying]);

    if (isFileSource) {
      if (!fileUrl) {
        return <div className="vidstack-player">No file URL provided</div>;
      }
    } else if (!cleanVideoId) {
      return (
        <div className="vidstack-player">No video ID provided</div>
      );
    }

    const mediaSrc = isFileSource ? fileUrl : `youtube/${cleanVideoId}`;

    return (
      <MediaPlayer
        ref={playerRef}
        src={mediaSrc}
        title={title || ""}
        playsInline
        autoPlay={autoplay && !isIOS}
        load={autoplay && !isIOS ? "eager" : "play"}
        onCanPlay={handleCanPlay}
        onTimeUpdate={handleTimeUpdate}
        onFullscreenChange={handleFullscreenChange}
        onFullscreenError={handleFullscreenError}
        onEnded={handleEnded}
        onPlaying={handlePlaying}
        className="vidstack-player"
        style={{
          backgroundImage: `url(${posterUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <MediaProvider />

        {subtitleUrl && (
          <Track
            src={subtitleUrl}
            kind="subtitles"
            label="English"
            lang="en"
          />
        )}

        <DefaultVideoLayout
          icons={defaultLayoutIcons}
          colorScheme="dark"
          slots={{
            centerControlsGroupCenter: <CenterPlayPauseFlash />,
            loadButton: (
              <PlayButton className="vds-load-play-button">
                <img src={playIconUrl} alt="Play" draggable={false} />
              </PlayButton>
            ),
          }}
        />
      </MediaPlayer>
    );
  },
);

VidstackPlayer.displayName = "VidstackPlayer";

export default VidstackPlayer;
